import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { FileText, Loader2, Building2, Printer, FileSpreadsheet } from 'lucide-react';
import { DrillDownDialog, BalanceSheet, IncomeStatement, GeneralLedger } from '@/components/reports';
import { printReport } from '@/utils/reportPrint';
import { exportReportToExcel } from '@/utils/reportExcel';
import { formatDate } from '@/utils/formatters';

import { API } from '../utils/api';

const Reports = () => {
  const [searchParams] = useSearchParams();
  const { getAuthHeaders } = useAuth();
  const [llcs, setLLCs] = useState([]);
  const [selectedLLC, setSelectedLLC] = useState(searchParams.get('llc') || '');
  const [reportType, setReportType] = useState(searchParams.get('type') || 'balance');
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [generalLedger, setGeneralLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const printRef = useRef(null);
  
  // Drill-down dialog state
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownData, setDrillDownData] = useState({ categoryName: '', transactions: [], total: 0 });
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  
  // Date range state
  const today = new Date().toISOString().split('T')[0];
  const currentYear = new Date().getFullYear();
  const urlYear = searchParams.get('year');
  const initialYear = urlYear ? parseInt(urlYear) : currentYear;
  const firstOfYear = new Date(initialYear, 0, 1).toISOString().split('T')[0];
  const endOfYear = initialYear === currentYear ? today : new Date(initialYear, 11, 31).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(firstOfYear);
  const [endDate, setEndDate] = useState(endOfYear);
  const [balanceAsOfDate, setBalanceAsOfDate] = useState(endOfYear);
  const [selectedYear, setSelectedYear] = useState(initialYear.toString());

  const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear - 3 + i).filter(y => y <= currentYear + 2);

  const handleYearChange = (year) => {
    setSelectedYear(year);
    const yearNum = parseInt(year);
    const yearStart = new Date(yearNum, 0, 1).toISOString().split('T')[0];
    const yearEnd = yearNum === currentYear 
      ? today 
      : new Date(yearNum, 11, 31).toISOString().split('T')[0];
    setStartDate(yearStart);
    setEndDate(yearEnd);
  };

  const fetchLLCs = async () => {
    try {
      const response = await axios.get(`${API}/llcs`, { headers: getAuthHeaders() });
      setLLCs(response.data);
      if (!selectedLLC && response.data.length > 0) {
        setSelectedLLC(response.data[0].id);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load LLCs');
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    if (!selectedLLC) return;
    
    setReportLoading(true);
    try {
      if (reportType === 'balance') {
        const response = await axios.get(
          `${API}/reports/balance-sheet/${selectedLLC}?as_of_date=${balanceAsOfDate}`, 
          { headers: getAuthHeaders() }
        );
        setBalanceSheet(response.data);
      } else if (reportType === 'income') {
        const response = await axios.get(
          `${API}/reports/income-statement/${selectedLLC}?start_date=${startDate}&end_date=${endDate}`, 
          { headers: getAuthHeaders() }
        );
        setIncomeStatement(response.data);
      } else if (reportType === 'ledger') {
        const response = await axios.get(
          `${API}/reports/general-ledger/${selectedLLC}?start_date=${startDate}&end_date=${endDate}`, 
          { headers: getAuthHeaders() }
        );
        setGeneralLedger(response.data);
      }
    } catch (error) {
      console.error('Report error:', error);
      toast.error('Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    fetchLLCs();
  }, []);

  useEffect(() => {
    if (selectedLLC) {
      fetchReport();
    }
  }, [selectedLLC, reportType, startDate, endDate, balanceAsOfDate]);

  const handlePrint = () => {
    if (!selectedLLC) return;
    const llcName = llcs.find(l => l.id === selectedLLC)?.name || 'Report';
    printReport({
      reportType,
      llcName,
      balanceSheet,
      incomeStatement,
      generalLedger,
      startDate,
      endDate,
      balanceAsOfDate,
    });
  };

  const handleDrillDown = async (categoryId, categoryName, total) => {
    if (!selectedLLC || total === 0) return;
    
    setDrillDownLoading(true);
    setDrillDownOpen(true);
    setDrillDownData({ categoryName, transactions: [], total });
    
    try {
      const isIncomeStatement = reportType === 'income';
      const dateParams = isIncomeStatement 
        ? `start_date=${startDate}&end_date=${endDate}`
        : `as_of_date=${balanceAsOfDate}`;
      
      const response = await axios.get(
        `${API}/reports/drilldown/${selectedLLC}?category_id=${categoryId}&${dateParams}`,
        { headers: getAuthHeaders() }
      );
      
      setDrillDownData({ 
        categoryName, 
        transactions: response.data.transactions || [], 
        total: response.data.total || total 
      });
    } catch (error) {
      console.error('Drill-down error:', error);
      toast.error('Failed to load transaction details');
    } finally {
      setDrillDownLoading(false);
    }
  };

  const currentLLC = llcs.find(l => l.id === selectedLLC);

  // Excel export function
  const handleExportExcel = () => {
    if (!selectedLLC) return;
    const llcName = llcs.find(l => l.id === selectedLLC)?.name || 'Report';
    exportReportToExcel({
      reportType,
      llcName,
      balanceSheet,
      incomeStatement,
      generalLedger,
      startDate,
      endDate,
      balanceAsOfDate,
    });
    toast.success('Excel file downloaded');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-900">
            Financial Reports
          </h1>
          <p className="text-slate-500 mt-1">
            Generate balance sheets and income statements
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportExcel}
            disabled={!selectedLLC || reportLoading}
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button
            variant="outline"
            onClick={handlePrint}
            disabled={!selectedLLC || reportLoading}
            data-testid="print-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-wrap lg:flex-nowrap gap-4 items-end">
            <div className="w-full sm:w-auto sm:min-w-[180px] lg:max-w-[220px]">
              <Label className="text-xs text-slate-500 mb-1.5 block">Select LLC</Label>
              <Select value={selectedLLC} onValueChange={setSelectedLLC}>
                <SelectTrigger data-testid="report-llc-select">
                  <SelectValue placeholder="Select an LLC" />
                </SelectTrigger>
                <SelectContent>
                  {llcs.map((llc) => (
                    <SelectItem key={llc.id} value={llc.id}>{llc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-auto sm:min-w-[160px]">
              <Label className="text-xs text-slate-500 mb-1.5 block">Report Type</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger data-testid="report-type-select">
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="balance">Balance Sheet</SelectItem>
                  <SelectItem value="income">Income Statement</SelectItem>
                  <SelectItem value="ledger">General Ledger</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {reportType === 'balance' && (
              <div className="w-full sm:w-auto sm:min-w-[150px]">
                <Label className="text-xs text-slate-500 mb-1.5 block">As of Date</Label>
                <Input
                  type="date"
                  value={balanceAsOfDate}
                  onChange={(e) => setBalanceAsOfDate(e.target.value)}
                  className="w-full"
                  data-testid="balance-date-input"
                />
              </div>
            )}
            
            {(reportType === 'income' || reportType === 'ledger') && (
              <>
                <div className="w-full sm:w-auto sm:min-w-[100px]">
                  <Label className="text-xs text-slate-500 mb-1.5 block">Year</Label>
                  <Select value={selectedYear} onValueChange={handleYearChange}>
                    <SelectTrigger data-testid="year-select">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {yearOptions.map((year) => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-full sm:w-auto sm:min-w-[150px]">
                  <Label className="text-xs text-slate-500 mb-1.5 block">Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full"
                    data-testid="start-date-input"
                  />
                </div>
                <div className="w-full sm:w-auto sm:min-w-[150px]">
                  <Label className="text-xs text-slate-500 mb-1.5 block">End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full"
                    data-testid="end-date-input"
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {!selectedLLC ? (
        <Card className="border-dashed border-2 border-slate-300">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="font-serif text-lg font-semibold text-slate-700">Select an LLC</h3>
            <p className="text-slate-500 mt-1">Choose an LLC to generate financial reports</p>
          </CardContent>
        </Card>
      ) : reportLoading ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-amber-700 mx-auto" />
            <p className="text-slate-500 mt-4">Generating report...</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Report Header */}
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="py-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-lg bg-slate-900 flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-serif font-semibold text-slate-900">
                    {currentLLC?.name}
                  </h2>
                  <p className="text-slate-500">
                    {reportType === 'balance' ? 'Balance Sheet' : reportType === 'ledger' ? 'General Ledger' : 'Income Statement'}
                    {' • '}
                    {reportType === 'balance' 
                      ? `As of ${formatDate(balanceAsOfDate)}`
                      : `Period: ${formatDate(startDate)} to ${formatDate(endDate)}`
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Report Content */}
          <div ref={printRef} className="print-content">
            {reportType === 'balance' && balanceSheet && (
              <BalanceSheet balanceSheet={balanceSheet} onDrillDown={handleDrillDown} />
            )}
            {reportType === 'income' && incomeStatement && (
              <IncomeStatement incomeStatement={incomeStatement} onDrillDown={handleDrillDown} />
            )}
            {reportType === 'ledger' && generalLedger && (
              <GeneralLedger generalLedger={generalLedger} />
            )}
          </div>
        </div>
      )}

      {/* Drill-Down Dialog */}
      <DrillDownDialog
        open={drillDownOpen}
        onOpenChange={setDrillDownOpen}
        categoryName={drillDownData.categoryName}
        transactions={drillDownData.transactions}
        total={drillDownData.total}
        loading={drillDownLoading}
      />
    </div>
  );
};

export default Reports;
